<template>
  <AppShell :hero-mode="!isLoggedIn">
    <!-- Guest hero -->
    <PageHero
      v-if="!isLoggedIn"
      eyebrow="AI Mentored"
      icon="fas fa-robot"
      title="AI Mentored Lab"
      subtitle="Stuck on a challenge? Get contextual hints and guidance from an AI mentor, 24/7, as you practice."
      cta="Try the AI Mentor"
      cta-href="/register"
      secondary="Browse Modules"
      secondary-href="/modules"
    />

    <div :class="isLoggedIn ? '' : 'container-app py-10'">
    <!-- Compact heading (authed) -->
    <div v-if="isLoggedIn" class="mb-8">
      <p class="eyebrow"><i class="fas fa-robot"></i> AI Mentored</p>
      <h1 class="mt-2 text-3xl font-extrabold text-white">AI Mentored Lab</h1>
      <p class="mt-2 text-ink-muted">Learn and practice on our labs with contextual AI guidance, available 24/7.</p>
    </div>

    <!-- Toolbar: search + view toggle -->
    <div class="flex flex-col gap-4 mb-6">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div class="relative sm:max-w-xs w-full">
          <i class="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint text-sm"></i>
          <input v-model="searchQuery" type="text" placeholder="Search labs..." class="input pl-10" />
        </div>
        <div class="flex items-center gap-1 p-1 rounded-lg bg-surface-light border border-line self-start">
          <button @click="viewMode = 'grid'" :class="['p-1.5 rounded-md transition-colors', viewMode === 'grid' ? 'bg-surface-lighter text-white' : 'text-ink-faint hover:text-ink-muted']" title="Grid view">
            <i class="fas fa-grip text-sm"></i>
          </button>
          <button @click="viewMode = 'list'" :class="['p-1.5 rounded-md transition-colors', viewMode === 'list' ? 'bg-surface-lighter text-white' : 'text-ink-faint hover:text-ink-muted']" title="List view">
            <i class="fas fa-list text-sm"></i>
          </button>
        </div>
      </div>
      <!-- Status filter chips -->
      <div class="flex flex-wrap gap-2">
        <button
          v-for="opt in statusOptions"
          :key="opt.value"
          @click="statusFilter = opt.value"
          :class="['chip cursor-pointer transition-colors', statusFilter === opt.value ? 'bg-brand/20 text-brand border-brand/40' : 'hover:border-ink-muted hover:text-ink']"
        >
          {{ opt.label }} <span class="ml-1 text-ink-faint">{{ statusCount(opt.value) }}</span>
        </button>
      </div>
    </div>

    <!-- Grid view -->
    <div v-if="visibleLabs.length && viewMode === 'grid'" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <a
        v-for="(lab, index) in visibleLabs"
        :key="index"
        :href="`/ai-labs/${lab.slug}`"
        class="card card-hover p-6 group flex flex-col"
      >
        <div class="flex items-center justify-between mb-4">
          <div class="h-11 w-11 grid place-items-center rounded-xl bg-brand/10 border border-brand/20 text-brand text-lg group-hover:scale-110 transition-transform">
            <i class="fas fa-robot"></i>
          </div>
          <span v-if="lab.isCompleted" class="badge bg-success/15 text-success">
            <i class="fas fa-check"></i> Done
          </span>
        </div>
        <h2 class="text-lg font-bold text-white mb-2 line-clamp-1">{{ lab.title }}</h2>
        <p class="text-sm text-ink-muted line-clamp-3 flex-1">{{ lab.description }}</p>
        <span class="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
          Start lab <i class="fas fa-arrow-right text-xs group-hover:translate-x-1 transition-transform"></i>
        </span>
      </a>
    </div>

    <!-- List view -->
    <div v-else-if="visibleLabs.length && viewMode === 'list'" class="card overflow-hidden divide-y divide-line">
      <a
        v-for="(lab, index) in visibleLabs"
        :key="index"
        :href="`/ai-labs/${lab.slug}`"
        class="flex items-center gap-4 p-4 hover:bg-surface-light transition-colors group"
      >
        <div class="h-10 w-10 grid place-items-center rounded-lg bg-brand/10 border border-brand/20 text-brand shrink-0">
          <i class="fas fa-robot"></i>
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2 flex-wrap">
            <h3 class="font-semibold text-white truncate">{{ lab.title }}</h3>
            <span v-if="lab.isCompleted" class="badge bg-success/15 text-success"><i class="fas fa-check"></i> Done</span>
          </div>
          <p class="text-sm text-ink-muted line-clamp-1 mt-0.5">{{ lab.description }}</p>
        </div>
        <i class="fas fa-arrow-right text-ink-faint group-hover:text-brand group-hover:translate-x-1 transition-all shrink-0"></i>
      </a>
    </div>

    <!-- Empty -->
    <div v-else class="card p-12 text-center">
      <i class="fas fa-robot text-4xl text-ink-faint mb-4"></i>
      <p class="text-ink-muted">No labs match your filters.</p>
    </div>
    </div>
  </AppShell>
</template>

<script>
import AppShell from "@/components/ui-components/AppShell.vue";
import PageHero from "@/components/ui-components/PageHero.vue";
import apiClient from "@/axiosConfig";
import { isAuthenticated } from "@/utils/auth";

export default {
  name: "AILabsPage",
  components: { AppShell, PageHero },
  data() {
    return {
      searchQuery: "",
      viewMode: "grid",
      statusFilter: "all",
      statusOptions: [
        { value: "all", label: "All" },
        { value: "available", label: "Available" },
        { value: "completed", label: "Completed" },
      ],
      labs: [],
    };
  },
  computed: {
    isLoggedIn() {
      return isAuthenticated();
    },
    filteredLabs() {
      const query = this.searchQuery.toLowerCase();
      return this.labs.filter(
        (lab) =>
          lab.title.toLowerCase().includes(query) || lab.description.toLowerCase().includes(query)
      );
    },
    visibleLabs() {
      return this.filteredLabs.filter((lab) => this.matchesStatus(lab, this.statusFilter));
    },
  },
  methods: {
    matchesStatus(lab, status) {
      if (status === "available") return !lab.isCompleted;
      if (status === "completed") return lab.isCompleted;
      return true;
    },
    statusCount(status) {
      return this.filteredLabs.filter((lab) => this.matchesStatus(lab, status)).length;
    },
    async fetchLabs() {
      try {
        const response = await apiClient.get("/ai-mentored-lab");
        this.labs = response.data.map((lab) => ({
          id: lab.id,
          title: lab.title,
          description: lab.description,
          isCompleted: lab.isCompleted,
          slug: lab.id,
        }));
      } catch (error) {
        console.error("Error fetching AI-Mentored Labs:", error);
      }
    },
  },
  mounted() {
    this.fetchLabs();
  },
};
</script>
